import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

const URL = `${SITE.url}/newsroom`;

export const Route = createFileRoute("/newsroom")({
  head: () => ({
    meta: [
      { title: `Newsroom — ${SITE.name}` },
      { name: "description", content: "Who we are, what we cover, and how to reach the Map Observer editorial team — the authoritative newsroom on Google Business Profile, the Map Pack, and local search." },
      { property: "og:title", content: "Inside the Map Observer newsroom" },
      { property: "og:description", content: "Editorial mission, masthead, and how to pitch tips or request comment on local search stories." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        name: SITE.name,
        url: SITE.url,
        diversityPolicy: `${SITE.url}/editorial-standards`,
        ethicsPolicy: `${SITE.url}/ethics`,
        correctionsPolicy: `${SITE.url}/corrections`,
        ownershipFundingInfo: `${SITE.url}/ownership-and-funding`,
        masthead: URL,
        knowsAbout: ["Local SEO", "Google Business Profile", "Google Maps", "Map Pack", "AI Overviews", "Ask Maps"],
        publishingPrinciples: `${SITE.url}/editorial-standards`,
      }),
    }],
  }),
  component: NewsroomPage,
});

function NewsroomPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">The newsroom</p>
        <h1 className="headline-display text-4xl sm:text-5xl mt-3">Inside Map Observer</h1>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Map Observer is the authoritative newsroom on Google Business Profile, the Google
          Map Pack, Google Maps, and the broader local-search industry. We report for the
          practitioners doing the work — local SEO agencies, GBP specialists, and the
          multi-location operators they serve — and set the record for how the industry
          understands what changed and why.
        </p>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">What we cover</h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed">
            <li>→ Google Business Profile product, policy, and suspension news</li>
            <li>→ Map Pack ranking shifts, algorithm updates, and volatility tracking</li>
            <li>→ Reviews, reputation, and Local Service Ads</li>
            <li>→ The impact of AI Overviews, AI Mode, and Ask Maps on local search</li>
            <li>→ Original research and benchmarks: <Link to="/state-of-local-search" className="text-accent underline">State of Local Search</Link>, <Link to="/local-search-index" className="text-accent underline">Local Search Index</Link></li>
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Masthead</h2>
          <div className="mt-6 space-y-6">
            <div>
              <p className="font-semibold">Map Observer Editorial</p>
              <p className="text-sm text-muted-foreground mt-1">
                Map Observer is produced by a small editorial team with a combined background
                in local SEO consulting, multi-location operations, and search-industry
                journalism. Every story is reviewed against our{" "}
                <Link to="/editorial-standards" className="text-accent underline">editorial standards</Link>
                {" "}before publication.
              </p>
            </div>
            <div className="border-t border-border pt-6">
              <p className="font-semibold">AI assistance, with human editing</p>
              <p className="text-sm text-muted-foreground mt-1">
                We use large language models to monitor sources, draft initial summaries, and
                surface candidate stories from across the industry. Every published article is
                edited and fact-checked against the cited primary sources before it goes live,
                and we mark AI-assisted reporting transparently in the byline area.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Pitch us a story or request comment</h2>
          <p className="mt-4 text-sm leading-relaxed">
            Have a tip about a Google Business Profile change, an unannounced algorithm shift,
            or a wave of suspensions? Are you a journalist looking for an expert source on
            local search, Map Pack volatility, or GBP policy? Reach the editorial desk:
          </p>
          <p className="mt-4 text-sm">
            <a href="mailto:editorial@mapobserver.com" className="text-accent underline">editorial@mapobserver.com</a>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            For brand assets, logos, and cite-this snippets, see our <Link to="/press" className="text-accent underline">press page</Link>.
          </p>
        </section>

        <section className="mt-12 grid sm:grid-cols-2 gap-4">
          <Link to="/editorial-standards" className="block border border-border p-5 hover:border-accent">
            <p className="eyebrow">Read next</p>
            <h3 className="font-semibold mt-1">Editorial standards</h3>
            <p className="text-xs text-muted-foreground mt-1">How we report, source, and verify.</p>
          </Link>
          <Link to="/corrections" className="block border border-border p-5 hover:border-accent">
            <p className="eyebrow">Read next</p>
            <h3 className="font-semibold mt-1">Corrections policy</h3>
            <p className="text-xs text-muted-foreground mt-1">How we handle mistakes and updates.</p>
          </Link>
          <Link to="/ownership-and-funding" className="block border border-border p-5 hover:border-accent">
            <p className="eyebrow">Read next</p>
            <h3 className="font-semibold mt-1">Ownership &amp; funding</h3>
            <p className="text-xs text-muted-foreground mt-1">Who pays for Map Observer.</p>
          </Link>
          <Link to="/ethics" className="block border border-border p-5 hover:border-accent">
            <p className="eyebrow">Read next</p>
            <h3 className="font-semibold mt-1">Ethics policy</h3>
            <p className="text-xs text-muted-foreground mt-1">Conflicts of interest and disclosures.</p>
          </Link>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
